import React, { useState } from "react";
import { Modal, Form, Input, Alert, List, Typography, Space, Tag } from "antd";
import { CommentOutlined } from "@ant-design/icons";
import { uldService } from "../../../../api/enhancedApi";

const { Text } = Typography;
const { TextArea } = Input;

/**
 * 批量更新ULD备注的弹窗组件
 */
const RemarksBatchUpdateModal = ({ visible, onCancel, onSuccess, selectedUlds = [] }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // 处理提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 提取ULD IDs
            const uldIdentifiers = selectedUlds.map(uld => uld.master.uld_identifier);
            
            // 调用批量更新API
            const response = await uldService.batchUpdateRemarks({
                uld_identifiers: uldIdentifiers,
                new_remarks: values.remarks
            });

            // 按照API_GUIDELINES规范处理响应格式
            const apiResult = response.data;
            const result = apiResult.data;
            
            if (result.success_count > 0) {
                if (result.failure_count > 0) {
                    Modal.info({
                        title: "批量更新完成",
                        content: `成功更新 ${result.success_count} 个ULD，失败 ${result.failure_count} 个`,
                    });
                } else {
                    Modal.success({
                        title: "批量更新成功",
                        content: `成功更新 ${result.success_count} 个ULD的备注`,
                    });
                }
                
                // 重置表单并关闭弹窗
                form.resetFields();
                onSuccess && onSuccess();
            } else {
                Modal.error({
                    title: "批量更新失败",
                    content: "所有ULD更新都失败了",
                });
            }
        } catch (error) {
            console.error("批量更新备注失败:", error);
            
            // 安全地处理错误信息
            let errorMessage = "请求失败";
            
            if (error.response?.data) {
                const errorData = error.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.detail) {
                    errorMessage = String(errorData.detail);
                } else if (errorData.message) {
                    errorMessage = String(errorData.message);
                } else {
                    try {
                        errorMessage = JSON.stringify(errorData);
                    } catch {
                        errorMessage = "解析错误信息失败";
                    }
                }
            } else if (error.message) {
                errorMessage = String(error.message);
            }
            
            Modal.error({
                title: "批量更新失败",
                content: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel && onCancel();
    };

    return (
        <Modal
            title={
                <Space>
                    <CommentOutlined />
                    批量更新备注
                </Space>
            }
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            okText="确认更新"
            cancelText="取消"
            confirmLoading={loading}
            width={600}
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                preserve={false}
            >
                <Alert
                    message={`将要更新 ${selectedUlds.length} 个ULD的备注信息`}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <Form.Item
                    name="remarks"
                    label="新的备注内容"
                    rules={[
                        { max: 500, message: "备注内容不能超过500个字符" }
                    ]}
                >
                    <TextArea
                        rows={4}
                        placeholder="请输入新的备注内容（留空表示清除所有备注）"
                        showCount
                        maxLength={500}
                    />
                </Form.Item>

                {/* 显示选中的ULD列表 */}
                <Form.Item label="选中的ULD">
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 4, padding: 8 }}>
                        <List
                            size="small"
                            dataSource={selectedUlds}
                            renderItem={(item) => {
                                // 安全获取数据，确保所有值都被转换为字符串
                                const identifier = String(item?.master?.uld_identifier || "未知");
                                const uldType = String(item?.master?.uld_type || "未知");
                                const remarks = item?.master?.remarks ? String(item.master.remarks) : null;
                                
                                return (
                                    <List.Item>
                                        <Space>
                                            <Text strong>{identifier}</Text>
                                            <Text type="secondary">({uldType})</Text>
                                            {remarks && (
                                                <Text type="secondary" ellipsis style={{ maxWidth: 200 }}>
                                                    当前备注: {remarks}
                                                </Text>
                                            )}
                                        </Space>
                                    </List.Item>
                                );
                            }}
                        />
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default RemarksBatchUpdateModal; 